import { FAIcon } from '@/components'
import { cn } from '@/lib/utils'
import { useLocalStorageSetting } from '@/utils'
import type { FontAwesomeIcon } from '@ur-solutions/react-components'
import type { PropsWithChildren, ReactNode } from 'react'

interface Props extends PropsWithChildren {
  type: 'statistics' | 'attachments' | 'logs'
  title: string
  icon: FontAwesomeIcon
  className?: string
  headerRight?: ReactNode
}

/** A collapsible card like the Absence page's; each card type remembers
 * whether it was collapsed. */
export function InfringementDetailCard({
  children,
  className,
  type,
  title,
  icon,
  headerRight,
}: Props) {
  const [collapsed, updateCollapsed] = useLocalStorageSetting(
    `infringementCardCollapsed.${type}`
  )

  return (
    <section
      className={cn(
        'overflow-hidden rounded-2xl bg-white shadow-card',
        className
      )}
    >
      <header
        className={cn(
          'flex items-center justify-between gap-3 px-5 py-4 select-none sm:px-6',
          !collapsed && 'border-b border-stone-200'
        )}
        role="button"
        aria-expanded={!collapsed}
        onClick={() => updateCollapsed(value => !value)}
      >
        <div className="flex items-center gap-2">
          <FAIcon
            className="text-stone-500"
            icon={icon}
            type="regular"
            fixedWidth
          />
          <h3 className="text-lg font-semibold text-stone-900">{title}</h3>
        </div>

        <div className="flex items-center gap-3">
          {headerRight}
          <FAIcon
            className={cn(
              'text-stone-500 transition-transform hover:text-stone-700',
              !collapsed && 'rotate-180'
            )}
            icon="chevron-down"
          />
        </div>
      </header>

      {!collapsed && children}
    </section>
  )
}
